import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { MdUpload, MdAdd } from "react-icons/md";
import { FaTrash, FaLink, FaFile } from "react-icons/fa6";

import api from "../../../services/api";
import { readFileAsync } from "../../../utils";

import Modal from "../../../components/modal";
import ModalCreationDoc from "./modalCreationDoc";

export default function Files({ project, setProject }) {
  const [documents, setDocuments] = useState(project?.documents || []);

  useEffect(() => {
    setDocuments(project?.documents);
  }, [project?.documents]);

  const onSubmit = async (documents) => {
    try {
      const { data } = await api.put(`/project/${project._id}`, { documents });
      setProject(data);
      toast.success("Updated!");
    } catch (e) {
      console.log(e);
      toast.error("Some Error!");
    }
  };

  return (
    <div className="p-2">
      <Documents documents={documents} onChange={onSubmit} project={project} />
    </div>
  );
}

const Documents = ({ documents, onChange, project }) => {
  const { user } = useSelector((state) => state.Auth);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState({ id: null, name: "" });
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [newLink, setNewLink] = useState({ name: "", url: "" });

  const handleDelete = async (doc) => {
    if (!confirm("Are you sure ?")) return;
    const docs = [...documents];
    await onChange(docs.filter((d) => d._id !== doc._id));
  };

  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    if (!uploadedFiles.length) return;

    setUploading(true);
    try {
      // Prepare files for the multiple upload endpoint
      const filesToUpload = [];

      for (const file of uploadedFiles) {
        // Convert file to base64 for the API
        const filePromise = await readFileAsync(file);
        filesToUpload.push({ rawBody: filePromise, name: file.name });
      }

      // Use the file/multiple endpoint
      const { data: urls } = await api.post("/file/multiple", {
        files: filesToUpload,
        folder: `/project/${project._id}`,
      });

      // Create new document objects
      const newDocuments = urls.map((url, index) => {
        const file = index < uploadedFiles.length ? uploadedFiles[index] : null;
        const fileName = file?.name || `file-${index}`;
        return {
          name: fileName,
          url,
          userId: user._id,
          userName: user.name,
          userAvatar: user.avatar,
          created_at: new Date().toISOString(),
        };
      });

      // Update documents with new ones
      await onChange([...documents, ...newDocuments]);

      toast.success("Files uploaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error uploading files");
    } finally {
      setUploading(false);
    }
  };

  const handleStartEdit = (doc) => {
    setEditing({ id: doc._id, name: doc.name });
  };

  const handleSaveName = async (doc) => {
    const updatedDocs = documents.map((d) => {
      if (d._id === doc._id) {
        return { ...d, name: editing.name };
      }
      return d;
    });
    await onChange(updatedDocs);
    setEditing({ id: null, name: "" });
  };

  const handleAddLink = async () => {
    if (!newLink.name || !newLink.url) {
      toast.error("Please provide both name and URL");
      return;
    }

    try {
      // Ensure URL has protocol
      let formattedUrl = newLink.url;
      if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
        formattedUrl = 'https://' + formattedUrl;
      }

      // Create new document object for link
      const newDocument = {
        name: newLink.name,
        url: formattedUrl,
        userId: user._id,
        userName: user.name,
        userAvatar: user.avatar,
        created_at: new Date().toISOString(),
        isLink: true,
      };

      await onChange([...documents, newDocument]);
      setShowLinkModal(false);
      setNewLink({ name: "", url: "" });
      toast.success("Link added successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error adding link");
    }
  };

  return (
    <div>
      <div className="mb-4 flex gap-2">
        <div className="flex-1 border border-gray-300 rounded-md p-4">
          <div className="flex items-center justify-center w-full">
            <label
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer ${
                uploading ? "bg-gray-100 border-gray-400" : "border-gray-300 bg-gray-50 hover:bg-gray-100"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (uploading) return;

                const droppedFiles = e.dataTransfer.files;
                if (droppedFiles.length > 0) handleFileUpload({ target: { files: droppedFiles } });
              }}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                {uploading ? (
                  <>
                    <svg className="w-8 h-8 mb-4 text-gray-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">Uploading...</p>
                  </>
                ) : (
                  <>
                    <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PDF, DOCX, XLSX, etc.</p>
                  </>
                )}
              </div>
              <input id="dropzone-file" type="file" className="hidden" multiple onChange={handleFileUpload} disabled={uploading} />
            </label>
          </div>
        </div>
        
        <div className="flex-1 border border-gray-300 rounded-md p-4">
          <div className="flex items-center justify-center w-full">
            <button
              onClick={() => setShowLinkModal(true)}
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-gray-300 bg-gray-50 hover:bg-gray-100"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <FaLink className="w-8 h-8 mb-4 text-gray-500" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to add</span> a new link
                </p>
                <p className="text-xs text-gray-500">Add URLs to external resources</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      <Modal isOpen={showLinkModal} onClose={() => setShowLinkModal(false)} className="w-96">
        <div className="p-6">
          <h3 className="text-lg font-medium mb-4">Add New Link</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={newLink.name}
                onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                placeholder="Enter link name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <input
                type="url"
                value={newLink.url}
                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                placeholder="https://example.com"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {documents && documents.length > 0 && (
        <div className="mt-4">
          <h3 className="font-medium text-gray-700 mb-2">Project Documents</h3>
          <div className="space-y-2">
            {documents.map((document, index) => (
              <div key={document._id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-3 overflow-hidden">
                  {document.isLink ? (
                    <FaLink className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  ) : (
                    <svg
                      className="w-5 h-5 text-gray-500 flex-shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  )}
                  {editing.id === document._id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        className="text-sm border rounded px-2 py-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveName(document);
                          if (e.key === "Escape") setEditing({ id: null, name: "" });
                        }}
                      />
                      <button
                        onClick={() => handleSaveName(document)}
                        className="text-sm text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50">
                        Save
                      </button>
                      <button
                        onClick={() => setEditing({ id: null, name: "" })}
                        className="text-sm text-gray-600 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-50">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span 
                      className="text-sm font-medium text-gray-700 truncate cursor-pointer hover:text-blue-600"
                      onClick={() => handleStartEdit(document)}
                    >
                      {document.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {document.userAvatar && (
                    <div className="flex items-center mr-4">
                      <img src={document.userAvatar} className="rounded-full w-6 h-6 mr-1" alt="User Avatar" />
                      <span className="text-sm text-gray-500">{document.userName}</span>
                    </div>
                  )}
                  <span className="text-sm text-gray-500">{document?.created_at?.slice(0, 10)}</span>
                  <a 
                    href={document.url} 
                    target="_blank" 
                    rel="noopener noreferrer nofollow"
                    className={`text-sm px-2 py-1 rounded ${
                      document.isLink 
                        ? "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        : "text-green-600 hover:text-green-800 hover:bg-green-50"
                    }`}
                  >
                    View
                  </a>
                  <button type="button" onClick={() => handleDelete(document)} className="text-sm text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
